import { useState, useRef, useEffect, useCallback } from 'react';
import { findAnswer } from '../services/chatQAService';
import '../styles/landing.css';

const QUICK_SUGGESTIONS = [
  '¿Cuánto cuesta la licencia?',
  '¿Cómo se activa la licencia?',
  '¿Qué incluye la app?',
  '¿Cómo cancelo mi suscripción y solicito reembolso?',
  '¿Tienen soporte?',
];

const WELCOME_MESSAGE =
  '¡Hola! 👋 Soy el asistente virtual de Vortex EXANI-II. ' +
  'Puedo ayudarte con información de simuladores oficiales, áreas del Ceneval, módulos para universidades que usan el EXANI-II, suscripciones y soporte. ¿En qué te ayudo?';

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'agent', text: WELCOME_MESSAGE, sources: null },
  ]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle');
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || status === 'sending') return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed, sources: null }]);
    setInput('');
    setStatus('sending');

    try {
      await new Promise((resolve) => setTimeout(resolve, 400));

      const result = await findAnswer(trimmed);
      setMessages((prev) => [
        ...prev,
        { role: 'agent', text: result.reply, sources: result.sources },
      ]);
      setStatus('idle');
    } catch (err) {
      console.error('[ChatWidget] Error:', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          text: '⚠️ No logré procesar tu pregunta. Inténtalo de nuevo.',
          sources: null,
        },
      ]);
      setStatus('error');
    }
  }, [status]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleRetry = () => {
    sendMessage(messages[messages.length - 1]?.role === 'user'
      ? messages[messages.length - 1].text
      : input);
  };

  return (
    <div className="chat-widget">
      <button
        type="button"
        className={`chat-widget-toggle${isOpen ? ' is-open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Cerrar chat' : 'Abrir chat'}
      >
        {isOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <span className="chat-widget-agent-avatar">⚓</span>
            <div>
              <strong>Asistente Vortex</strong>
              <span className="chat-widget-online">
                <span className="chat-widget-online-dot" /> En línea 24/7
              </span>
            </div>
            <button
              type="button"
              className="chat-widget-close"
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="chat-widget-body" ref={messagesRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg--${m.role}`}>
                {m.role === 'agent' && <span className="chat-msg-agent-badge">⚓</span>}
                <span className="chat-msg-bubble">{m.text}</span>
                {m.sources && m.sources.length > 0 && (
                  <div className="chat-msg-sources">
                    <small>Fuentes:</small>
                    <div className="chat-msg-source-tags">
                      {m.sources.map((s, j) => (
                        <span key={j} className="source-tag">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {status === 'sending' && (
              <div className="chat-msg chat-msg--agent">
                <span className="chat-msg-agent-badge">⚓</span>
                <span className="chat-msg-bubble">
                  <i className="typing-indicator">
                    <span></span><span></span><span></span>
                  </i>
                </span>
              </div>
            )}
          </div>

          <div className="chat-widget-quick">
            {QUICK_SUGGESTIONS.map((q) => (
              <button key={q} type="button" onClick={() => sendMessage(q)} disabled={status === 'sending'}>
                {q}
              </button>
            ))}
          </div>

          <form className="chat-widget-form" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta..."
              disabled={status === 'sending'}
              autoComplete="off"
            />
            <button type="submit" disabled={status === 'sending' || !input.trim()}>
              {status === 'sending' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4v0a4 4 0 0 1-4-4 0 0 1 4-4 4 4 0 0 1 4 4v0" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </form>

          {status === 'error' && (
            <button
              type="button"
              className="chat-retry-btn"
              onClick={handleRetry}
            >
              Reintentar última pregunta
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ChatWidget;
